import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { saTodayISO } from '../../lib/dateUtils';
import { API_URL, auth } from './financeApi';

const yearStart = () => `${saTodayISO().slice(0, 4)}-01-01`;

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const ExportDialog = ({ open, onOpenChange, vehicles, token }) => {
  const [vehicleId, setVehicleId] = useState('all');
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(saTodayISO());
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open) { setStart(yearStart()); setEnd(saTodayISO()); }
  }, [open]);

  const run = async (format) => {
    setBusy(format);
    try {
      const params = { start, end, format, ...(vehicleId !== 'all' ? { vehicle_id: vehicleId } : {}) };
      const res = await axios.get(`${API_URL}/finance/export`, { ...auth(token), params, responseType: 'blob' });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      download(res.data, match ? match[1] : `ntwanayam-expenses.${format}`);
      toast.success(format === 'pdf' ? 'PDF downloaded' : 'Spreadsheet downloaded');
    } catch (e) {
      const text = e?.response?.data instanceof Blob ? await e.response.data.text() : '';
      let detail = 'Could not create the report';
      try { detail = JSON.parse(text).detail || detail; } catch { /* keep default */ }
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const valid = start && end && start <= end;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileDown className="w-5 h-5" /> Download for your accountant</DialogTitle>
          <DialogDescription>Totals per type and month, plus every expense with date, type, amount and who saved it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger data-testid="export-vehicle-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.registration_number}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="export-start">From</Label>
              <Input id="export-start" type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="export-start-input" />
            </div>
            <div>
              <Label htmlFor="export-end">To</Label>
              <Input id="export-end" type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="export-end-input" />
            </div>
          </div>
          {!valid && <p className="text-xs text-red-600" data-testid="export-date-error">The start date must be before the end date.</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={!valid || !!busy} onClick={() => run('xlsx')} data-testid="export-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> {busy === 'xlsx' ? 'Preparing…' : 'Spreadsheet'}
          </Button>
          <Button disabled={!valid || !!busy} onClick={() => run('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1" /> {busy === 'pdf' ? 'Preparing…' : 'PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
